import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LoadingIcon from 'components/atoms/LoadingIcon'
import notify from 'components/atoms/Notify'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import { trim, isArray, startsWith, forEach } from 'lodash'
import { Formik, Form } from 'formik'
import classNames from 'classnames'
import apiDelete from 'lib/network/apiDelete'
import moment from 'moment-timezone'
import useModal from 'hooks/useModal'
import GeneratePartnerKeyModal from './modals/GeneratePartnerKeyModal'
import GenerateWebhooksModal from './modals/GenerateWebhookModal'
import apiGet from 'lib/network/apiGet'
import { CameraIcon } from '@heroicons/react/solid'

const PartnerProfile = ({ readOnly, selfService = false, loading, refetch, partnerData, isEdit, id }) => {
  const { t, i18n } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    websiteUrl: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [webhooks, setWebhooks] = useState([])
  const [selectedWebhook, setSelectedWebhook] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState('')
  const [avatarFile, setAvatarFile] = useState(null)
  const avatarInputRef = useRef(null)

  const [params, setParams] = useState({ t: 1 })
  const isRTL = i18n.language === 'ar'

  const tabs = [
    { id: 1, name: t('partner.profile.tabs.details') },
    { id: 2, name: t('partner.profile.tabs.additionalInfo') },
    { id: 3, name: 'Web Hooks' },
  ]

  const history = useHistory()
  const fieldsReadOnly = readOnly
  const canManageIntegrations = !readOnly || selfService

  useEffect(() => {
    const fetchWebhooks = async () => {
      try {
        const res = await apiGet(`partner/${id}/webhooks`)
        if (res?.success && isArray(res?.data)) {
          setWebhooks(res.data)
        } else {
          setWebhooks([])
        }
      } catch (error) {
        console.error('Error fetching webhooks:', error)
        setWebhooks([])
      }
    }

    if (id) fetchWebhooks()
  }, [id, refetch])

  const handleGenerateKeySuccess = () => {
    closeModal()
    if (refetch) {
      refetch()
    }
  }

  const handleGenerateWebhookSuccess = () => {
    closeWebhookModal()
    closeEditWebhookModal()
    if (refetch) {
      refetch()
      if (id) {
        const fetchWebhooks = async () => {
          try {
            const res = await apiGet(`partner/${id}/webhooks`)
            if (res?.success && isArray(res?.data)) {
              setWebhooks(res.data)
            } else {
              setWebhooks([])
            }
          } catch (error) {
            console.error('Error fetching webhooks:', error)
            setWebhooks([])
          }
        }
        fetchWebhooks()
      }
    }
  }

  const handleEditWebhook = webhook => {
    setSelectedWebhook(webhook)
    openEditWebhookModal()
  }

  const handleDeleteWebhook = webhookId => {
    notify.deleteConfirm({
      title: 'Delete Webhook',
      message: 'Are you sure you want to delete this webhook? This action cannot be undone.',
      onConfirm: async () => {
        try {
          const response = await apiDelete(`webhook/${webhookId}`)
          if (response?.success) {
            notify.success('Webhook deleted successfully')
            if (refetch) {
              refetch()
              if (id) {
                const fetchWebhooks = async () => {
                  try {
                    const res = await apiGet(`partner/${id}/webhooks`)
                    if (res?.success && isArray(res?.data)) {
                      setWebhooks(res.data)
                    } else {
                      setWebhooks([])
                    }
                  } catch (error) {
                    console.error('Error fetching webhooks:', error)
                    setWebhooks([])
                  }
                }
                fetchWebhooks()
              }
            }
          } else {
            notify.error(response?.message || 'Failed to delete webhook')
          }
        } catch (err) {
          console.error(err)
          notify.error('Failed to delete webhook')
        }
      },
    })
  }

  const [GenerateKeyModal, openGenerateKeyModal, closeModal] = useModal({
    content: <GeneratePartnerKeyModal partnerId={id} onSuccess={handleGenerateKeySuccess} />,
    title: t('partner.profile.modals.generateKeyTitle'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [GenerateWebhookModal, openGenerateWebhookModal, closeWebhookModal] = useModal({
    content: <GenerateWebhooksModal partnerId={id} onSuccess={handleGenerateWebhookSuccess} />,
    title: 'Generate Webhook',
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [EditWebhookModal, openEditWebhookModal, closeEditWebhookModal] = useModal({
    content: <GenerateWebhooksModal partnerId={id} onSuccess={handleGenerateWebhookSuccess} isEdit={true} webhookData={selectedWebhook} />,
    title: 'Edit Webhook',
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  useEffect(() => {
    if (partnerData) {
      setFormData({
        name: partnerData.name || '',
        email: partnerData.email || '',
        phone: partnerData.phone || '',
        websiteUrl: partnerData.websiteUrl || '',
      })
      setAvatarPreview(partnerData.avatar || '')
      setAvatarFile(null)
    } else {
      setAvatarPreview('')
      setAvatarFile(null)
    }
  }, [partnerData])

  useEffect(() => {
    return () => {
      if (avatarPreview && startsWith(avatarPreview, 'blob:')) {
        URL.revokeObjectURL(avatarPreview)
      }
    }
  }, [avatarPreview])

  const setCurrentTab = currentTab => setParams({ t: parseInt(currentTab, 10) })

  const validateForm = () => {
    if (!trim(formData.name)) {
      notify.error(t('partner.profile.validation.nameRequired'))
      return false
    }
    if (!trim(formData.email)) {
      notify.error(t('partner.profile.validation.emailRequired'))
      return false
    }
    if (!trim(formData.phone)) {
      notify.error(t('partner.profile.validation.phoneRequired'))
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      notify.error(t('partner.profile.validation.emailInvalid'))
      return false
    }

    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/
    const cleanPhone = formData.phone.replace(/[\s-()]/g, '')
    if (!phoneRegex.test(cleanPhone)) {
      notify.error(t('partner.profile.validation.phoneInvalid'))
      return false
    }

    return true
  }

  const handleSavePartner = async (values, { setSubmitting }) => {
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const endpoint = id ? `partner/${id}` : 'add/partner'
      const apiMethod = id ? apiPut : apiPost
      const payload = new FormData()
      forEach(formData, (value, key) => {
        payload.append(key, value ?? '')
      })
      if (avatarFile) {
        payload.append('avatar', avatarFile)
      }

      const response = await apiMethod(endpoint, payload)

      if (response.success) {
        const message = id ? t('partner.profile.messages.partnerUpdated') : t('partner.profile.messages.partnerAdded')
        notify.success(message)
        if (response?.data?.avatar) {
          setAvatarPreview(response.data.avatar)
        }
        setAvatarFile(null)

        if (refetch) {
          refetch()
        }

        if (!selfService) {
          history.push('/partners')
        }
      } else {
        notify.error(response.message || t('partner.profile.messages.saveFailed'))
      }
    } catch (error) {
      notify.error(t('partner.profile.messages.saveFailed'))
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  const deletePartner = () => {
    notify.deleteConfirm({
      title: t('partner.profile.modals.deleteTitle'),
      message: t('partner.profile.messages.partnerDeleteConfirm'),
      onConfirm: async () => {
        const response = await apiDelete(`partner/${id}`)
        if (response?.success) {
          history.push('/partners')
          notify.success(t('partner.profile.messages.partnerDeleted'))
        }
      },
    })
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const initialValues = isEdit
    ? partnerData
    : {
        name: '',
        email: '',
        phone: '',
        websiteUrl: '',
      }

  const handleAvatarChange = event => {
    const file = event.target.files[0]
    if (!file) return
    setAvatarFile(file)
    const previewUrl = URL.createObjectURL(file)
    setAvatarPreview(previewUrl)
  }

  const triggerAvatarPicker = () => {
    if (fieldsReadOnly) return
    avatarInputRef.current?.click()
  }

  const formatDate = dateString => {
    if (!dateString) return ''
    return moment(dateString).format('DD MMMM YYYY, hh:mm A')
  }

  const isExpired = expiryDate => {
    if (!expiryDate) return false
    return moment().isAfter(moment(expiryDate))
  }

  if (loading) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center">
        <LoadingIcon />
      </div>
    )
  }

  const handleRevokeKey = keyId => {
    notify.deleteConfirm({
      title: t('partner.profile.modals.revokeTitle'),
      message: t('partner.profile.messages.keyRevokeConfirm'),
      onConfirm: async () => {
        try {
          const response = await apiDelete(`partner-key/${id}/${keyId}`)

          if (response?.success) {
            notify.success(t('partner.profile.messages.keyRevoked'))

            if (refetch) {
              await refetch()
            }
          } else {
            notify.error(response?.message || t('partner.profile.messages.keyRevokeFailed'))
          }
        } catch (err) {
          console.error(err)
          notify.error(t('partner.profile.messages.keyRevokeFailed'))
        }
      },
    })
  }

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={handleSavePartner} enableReinitialize>
        {({ values, isValid, setFieldValue }) => (
          <div className="w-full mb-[60px] flex flex-col">
            <div className="bg-white flex w-full">
              <Form className="space-y-8 py-8 w-full mx-auto px-8">
                <div>
                  <div className="border-b border-gray-200 pb-5 sm:pb-0">
                    <h3 className="text-xl font-medium leading-6 text-gray-900">
                      {selfService ? t('sidebar.partnerSettings') : t('partner.profile.title')} {readOnly ? t('partner.profile.readOnly') : ''}
                    </h3>
                    <div className="mt-3 sm:mt-4">
                      {/* Desktop tab navigation */}
                      <div className="hidden sm:block">
                        <nav className={classNames('-mb-px flex', isRTL ? 'space-x-8 space-x-reverse' : 'space-x-8')}>
                          {tabs.map(tab => (
                            <span
                              key={tab.name}
                              onClick={() => setCurrentTab(tab.id)}
                              className={classNames(
                                tab.id === params?.t ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                                'whitespace-nowrap pb-4 border-b-2 font-medium text-sm cursor-pointer'
                              )}
                              aria-current={params?.t === tab.id ? 'page' : undefined}
                            >
                              {tab.name}
                            </span>
                          ))}
                        </nav>
                      </div>

                      {/* Mobile tab navigation - vertical style like user profile */}
                      <div className="sm:hidden">
                        <div className="space-y-2">
                          {tabs.map(tab => (
                            <div
                              key={tab.id}
                              onClick={() => setCurrentTab(tab.id)}
                              className={classNames(
                                tab.id === params?.t ? 'bg-indigo-100 text-indigo-700 rounded-lg' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg',
                                'px-4 py-3 cursor-pointer transition-colors duration-200'
                              )}
                            >
                              <span className="font-medium text-sm">{tab.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {params?.t === 1 && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-center sm:pt-5">
                        <label htmlFor="partnerAvatar" className="block text-sm font-medium text-gray-700">
                          {t('partner.profile.form.logo')}
                        </label>
                        <div className="mt-1 sm:mt-0 sm:col-span-2">
                          <div className="flex pb-4 items-center">
                            <div className="bg-gray relative h-[200px] w-[150px] border mr-4 border-dashed border-gray-300 rounded-md">
                              <input
                                id="partnerAvatar"
                                ref={avatarInputRef}
                                name="avatar"
                                className="hidden"
                                accept=".jpg,.jpeg,.png,.webp"
                                type="file"
                                disabled={fieldsReadOnly}
                                onChange={handleAvatarChange}
                              />
                              {avatarPreview ? (
                                <span onClick={!fieldsReadOnly ? triggerAvatarPicker : undefined} className={!fieldsReadOnly ? 'cursor-pointer' : ''}>
                                  <img src={avatarPreview} alt="" className="bg-cover h-full w-[150px] rounded object-cover" />
                                </span>
                              ) : (
                                <div
                                  onClick={!fieldsReadOnly ? triggerAvatarPicker : undefined}
                                  className={classNames(
                                    'bg-white flex p-2 items-center justify-center bg-contain bg-no-repeat bg-center border-gray-300 rounded-md h-full w-full',
                                    fieldsReadOnly ? 'cursor-default opacity-60' : 'cursor-pointer'
                                  )}
                                >
                                  <CameraIcon className="text-gray-400 h-8 w-8" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('partner.profile.form.name')} *
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={fieldsReadOnly}
                            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder={t('partner.profile.form.namePlaceholder')}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('partner.profile.form.email')} *
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={fieldsReadOnly}
                            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder={t('partner.profile.form.emailPlaceholder')}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="phone" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('partner.profile.form.phone')} *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            id="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            disabled={fieldsReadOnly}
                            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder={t('partner.profile.form.phonePlaceholder')}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="websiteUrl" className="block text-sm font-normal text-gray-700 mb-1">
                            {t('partner.profile.form.websiteUrl')}
                          </label>
                          <input
                            type="url"
                            name="websiteUrl"
                            id="websiteUrl"
                            value={formData.websiteUrl}
                            onChange={handleChange}
                            disabled={fieldsReadOnly}
                            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder={t('partner.profile.form.websitePlaceholder')}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {params?.t === 2 && (
                    <>
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="authentication" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            {t('partner.profile.additionalInfo.authKeys')}
                          </label>
                          <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:mt-0 w-full max-w-lg sm:col-span-2">
                            <span className="mr-2 mb-2 sm:mb-0">
                              {partnerData?.authenticationKey?.length || 0} {t('partner.profile.additionalInfo.activeKeys')}
                            </span>
                            {canManageIntegrations && (
                              <button
                                type="button"
                                onClick={openGenerateKeyModal}
                                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 w-full sm:w-auto"
                              >
                                {t('partner.profile.buttons.generateKey')}
                              </button>
                            )}
                          </div>
                        </div>

                        {partnerData?.authenticationKey?.map((auth, index) => (
                          <div key={auth._id || index}>
                            <div className="sm:grid border-t sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                              <label htmlFor="authenticationKey" className="block text-sm font-bold text-normal text-gray-700 sm:mt-px sm:pt-2 mb-2 sm:mb-0">
                                {t('partner.profile.additionalInfo.keyNumber')}
                                {index + 1}
                              </label>
                              <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                                <div className="flex-1">
                                  <div>
                                    <strong className="block text-xs text-gray-600 mb-1">{t('partner.profile.additionalInfo.privateKey')}</strong>
                                    <div className="font-mono text-xs sm:text-sm bg-gray-100 p-2 rounded border break-all">{auth.privateKey}</div>
                                  </div>

                                  <div className="mt-3">
                                    <strong className="block text-xs text-gray-600 mb-1">{t('partner.profile.additionalInfo.publicKey')}</strong>
                                    <div className="font-mono text-xs sm:text-sm bg-gray-100 p-2 rounded border break-all">{auth.publicKey}</div>
                                  </div>

                                  <div className="mt-2 text-xs text-gray-600">
                                    <div className="flex flex-col sm:flex-row sm:items-center">
                                      <strong>{t('partner.profile.additionalInfo.expiryDate')}</strong>
                                      <span className="sm:ml-1">{formatDate(auth.expiryDate)}</span>
                                      {isExpired(auth.expiryDate) ? (
                                        <span className="mt-1 sm:mt-0 sm:ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          {t('partner.profile.additionalInfo.expired')}
                                        </span>
                                      ) : (
                                        <span className="mt-1 sm:mt-0 sm:ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          {t('partner.profile.additionalInfo.active')}
                                        </span>
                                      )}
                                    </div>
                                    <div className="mt-1">
                                      <strong>{t('partner.profile.additionalInfo.created')}</strong> {formatDate(auth.createdAt)}
                                    </div>
                                    <div className="mt-1">
                                      <strong>{t('partner.profile.additionalInfo.updated')}</strong> {formatDate(auth.updatedAt)}
                                    </div>
                                  </div>
                                  {canManageIntegrations && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        onClick={() => handleRevokeKey(auth._id)}
                                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 w-full sm:w-auto"
                                      >
                                        {t('partner.profile.buttons.revokeKey')}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        {(!partnerData?.authenticationKey || partnerData.authenticationKey.length === 0) && (
                          <div className="sm:grid border-t sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                            <label htmlFor="noKey" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2 mb-2 sm:mb-0">
                              {t('partner.profile.additionalInfo.noKeys')}
                            </label>
                            <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                              <div className="text-gray-500 text-sm">{t('partner.profile.additionalInfo.noKeysMessage')}</div>
                            </div>
                          </div>
                        )}

                        <div className="sm:grid border-t sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="partnerCreated" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2 mb-2 sm:mb-0">
                            {t('partner.profile.additionalInfo.partnerCreated')}
                          </label>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">{formatDate(partnerData?.createdAt)}</div>
                        </div>

                        <div className="sm:grid border-t sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="partnerUpdated" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2 mb-2 sm:mb-0">
                            {t('partner.profile.additionalInfo.partnerUpdated')}
                          </label>
                          <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">{formatDate(partnerData?.updatedAt)}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {params?.t === 3 && (
                    <>
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                          <label htmlFor="webhooks" className="block font-bold text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                            Partner Webhooks
                          </label>
                          <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:mt-0 w-full max-w-lg sm:col-span-2">
                            <span className="mr-2 mb-2 sm:mb-0">{webhooks?.length || 0} Active Webhooks</span>
                            {canManageIntegrations && (
                              <button
                                type="button"
                                onClick={openGenerateWebhookModal}
                                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 w-full sm:w-auto"
                              >
                                Generate Webhook
                              </button>
                            )}
                          </div>
                        </div>

                        {/* {webhooks?.map((webhook, index) => (
                          <div key={webhook._id || index}>
                            <div className="sm:grid border-t sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                              <label htmlFor="webhook" className="block text-sm font-bold text-normal text-gray-700 sm:mt-px sm:pt-2 mb-2 sm:mb-0">
                                Webhook {index + 1}
                              </label>
                              <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                                <div className="flex-1">
                                  <div>
                                    <strong className="block text-xs text-gray-600 mb-1">Name</strong>
                                    <div className="font-mono text-xs sm:text-sm bg-gray-100 p-2 rounded border break-all">{webhook.name}</div>
                                  </div>

                                  <div className="mt-3">
                                    <strong className="block text-xs text-gray-600 mb-1">Webhook URL</strong>
                                    <div className="font-mono text-xs sm:text-sm bg-gray-100 p-2 rounded border break-all">{webhook.webhookUrl}</div>
                                  </div>

                                  {webhook.description && (
                                    <div className="mt-3">
                                      <strong className="block text-xs text-gray-600 mb-1">Description</strong>
                                      <div className="text-xs sm:text-sm bg-gray-100 p-2 rounded border break-all">{webhook.description}</div>
                                    </div>
                                  )}

                                  <div className="mt-2 text-xs text-gray-600">
                                    <div className="flex flex-col sm:flex-row sm:items-center">
                                      <strong>Status</strong>
                                      <span className="sm:ml-1">
                                        {webhook.isEnabled ? (
                                          <span className="mt-1 sm:mt-0 sm:ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Enabled
                                          </span>
                                        ) : (
                                          <span className="mt-1 sm:mt-0 sm:ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            Disabled
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                    <div className="mt-1">
                                      <strong>Created</strong> {formatDate(webhook.createdAt)}
                                    </div>
                                    <div className="mt-1">
                                      <strong>Updated</strong> {formatDate(webhook.updatedAt)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))} */}
                        {webhooks?.map((webhook, index) => (
                          <div key={webhook._id} className="flex items-center justify-between border-b py-4">
                            <div className="flex-1">
                              <div className="font-semibold">{webhook.name}</div>
                              <div className="text-sm text-gray-500">{webhook.webhookUrl}</div>
                              {webhook.description && <div className="text-xs text-gray-400 mt-1">{webhook.description}</div>}
                              <div className="mt-2 text-xs text-gray-600">
                                <div className="flex flex-col sm:flex-row sm:items-center">
                                  <strong>Status:</strong>
                                  <span className="sm:ml-1">
                                    <span
                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        webhook.isEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                      }`}
                                    >
                                      {webhook.isEnabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                  </span>
                                </div>
                                <div className="mt-1">
                                  <strong>Created:</strong> {formatDate(webhook.createdAt)}
                                </div>
                                <div className="mt-1">
                                  <strong>Updated:</strong> {formatDate(webhook.updatedAt)}
                                </div>
                              </div>
                            </div>
                            {canManageIntegrations && (
                              <div className="flex flex-col sm:flex-row gap-2 ml-4">
                                <button
                                  type="button"
                                  onClick={() => handleEditWebhook(webhook)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteWebhook(webhook._id)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}

                        {webhooks.length === 0 && <div className="text-gray-500 text-sm mt-3">No webhooks found.</div>}
                      </div>
                    </>
                  )}
                </div>

                {params?.t !== 2 && params?.t !== 3 && !readOnly && (
                  <div className="pt-2">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={classNames(
                          isSubmitting && 'cursor-not-allowed opacity-50',
                          'inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 w-full sm:w-auto'
                        )}
                      >
                        {isSubmitting ? (
                          <span className="flex items-start">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span>{t('partner.profile.buttons.pleaseWait')}</span>
                          </span>
                        ) : (
                          <span>{!isEdit ? t('partner.profile.buttons.addPartner') : t('partner.profile.buttons.updatePartner')}</span>
                        )}
                      </button>

                      {isEdit && !selfService && (
                        <button
                          type="button"
                          onClick={deletePartner}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-normal rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 w-full sm:w-auto"
                        >
                          {t('partner.profile.buttons.deletePartner')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </Form>
            </div>
          </div>
        )}
      </Formik>
      <GenerateKeyModal />
      <GenerateWebhookModal />
      <EditWebhookModal />
    </>
  )
}

export default PartnerProfile
